import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import toast from 'react-hot-toast'
import { projectsService, ProjectCreate } from '../../services/projects.service'

export default function ProjectsPage() {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [showForm, setShowForm] = useState(false)
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')

  const { data: projects = [], isLoading } = useQuery({
    queryKey: ['projects'],
    queryFn: () => projectsService.getProjects(),
  })

  const createMutation = useMutation({
    mutationFn: (data: ProjectCreate) => projectsService.createProject(data),
    onSuccess: (project) => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      setShowForm(false)
      setName('')
      setDescription('')
      navigate(`/projects/${project.id}`)
    },
    onError: () => toast.error('Ошибка создания проекта'),
  })

  const deleteMutation = useMutation({
    mutationFn: (id: number) => projectsService.deleteProject(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['projects'] })
      toast.success('Проект удалён')
    },
  })

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Проекты</h1>
          <p className="text-sm text-gray-500 mt-1">
            Группировка каналов, лидов и кампаний по бизнес-проектам
          </p>
        </div>
        <button
          onClick={() => setShowForm(true)}
          className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
        >
          + Новый проект
        </button>
      </div>

      {showForm && (
        <form
          onSubmit={e => { e.preventDefault(); createMutation.mutate({ name, description: description || undefined }) }}
          className="bg-white rounded-lg border p-5 space-y-3"
        >
          <input
            type="text" value={name} onChange={e => setName(e.target.value)}
            placeholder="Название проекта" required
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          <textarea
            value={description} onChange={e => setDescription(e.target.value)}
            placeholder="Описание (необязательно)" rows={2}
            className="w-full border rounded-lg px-3 py-2 text-sm"
          />
          <div className="flex gap-2">
            <button type="submit" className="px-4 py-2 bg-primary-600 text-white text-sm rounded-lg">
              Создать
            </button>
            <button type="button" onClick={() => setShowForm(false)}
              className="px-4 py-2 border text-gray-700 text-sm rounded-lg">
              Отмена
            </button>
          </div>
        </form>
      )}

      {isLoading ? (
        <div className="text-gray-500">Загрузка...</div>
      ) : projects.length > 0 ? (
        <div className="grid gap-4 md:grid-cols-2">
          {projects.map(p => (
            <div
              key={p.id}
              onClick={() => navigate(`/projects/${p.id}`)}
              className="bg-white rounded-lg border p-5 hover:shadow-md transition-shadow cursor-pointer"
            >
              <h3 className="font-semibold text-lg text-gray-900">{p.name}</h3>
              {p.description && <p className="text-sm text-gray-500 mt-1 line-clamp-2">{p.description}</p>}
              <div className="flex items-center gap-4 mt-3 text-sm text-gray-500">
                <span>{p.channels_count} каналов</span>
                <span>{p.leads_count} лидов</span>
                <span>{new Date(p.created_at).toLocaleDateString('ru-RU')}</span>
              </div>
              <button
                onClick={e => { e.stopPropagation(); deleteMutation.mutate(p.id) }}
                className="mt-2 text-xs text-red-500 hover:text-red-700"
              >
                Удалить
              </button>
            </div>
          ))}
        </div>
      ) : !showForm ? (
        <div className="text-center py-16 text-gray-400">
          Нет проектов. Создайте первый для управления сидингом и лидогенерацией.
        </div>
      ) : null}
    </div>
  )
}
